import { css } from "@emotion/css";
import {
  Box,
  Button,
  Colors,
  DatePicker,
  OutlinedInput,
  InputAdornment as QuilInputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  InputProps as QuilInputProps,
} from "@analyticsMD/quil";
import moment from "moment";
import EventIcon from "@mui/icons-material/Event";

import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useMutation, useQueryClient } from "react-query";
import { hideIntelligenceText, patchEDD, postEDD } from "../api/endpoints";
import IntelligenceText from "../types/IntelligenceText";
import { Sources } from "../types/Sources";
import { TypeChoices } from "../types/TypeChoices";
import { ActionTrack } from "../types/Analytics";
import { grey } from "@mui/material/colors";
import EDD from "../types/EDD";
import { AnalyticsBrowser } from "@segment/analytics-next";
import {
  Box as MuiBox,
  FormControl,
  Input,
  InputAdornment,
} from "@mui/material";
import type { InputProps as MuiRootInputProps } from "@mui/material/Input";
import { UI_ACCENT_BLUE, UI_ACCENT_BLUE_HOVER } from "../constants/ui-colors";

const sxStyles = {
  dayPicker: {
    display: "flex",
    padding: "0.5rem",
    "& .MuiPickersDay-root": {
      "&:hover": {
        backgroundColor: UI_ACCENT_BLUE_HOVER,
      },
      "&.Mui-selected": {
        backgroundColor: `${UI_ACCENT_BLUE} !important`,
        color: "#fff !important",
        boxShadow: "0 1px 3px 0 rgba(25, 118, 210, 0.35)",
        borderRadius: "50%",
        "&:hover": {
          backgroundColor: `${UI_ACCENT_BLUE} !important`,
          color: "#fff !important",
        },
        "& .MuiTypography-root, & .MuiPickersDay-day": {
          color: "#fff !important",
        },
      },
      "&.MuiPickersDay-today:not(.Mui-selected)": {
        border: `1px solid ${UI_ACCENT_BLUE}`,
      },
    },
  },
  inputWrapper: {
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  icon: {
    fontSize: ".5rem",
    marginLeft: "-0.5rem",
    color: `${grey[900]} !important`,
  },
  inlineIcon: {
    fontSize: ".5rem",
    marginRight: "1rem",
    color: `${grey[900]} !important`,
  },
  doneButton: {
    boxShadow: "none",
    fontSize: "0.9rem",
    color: UI_ACCENT_BLUE,
    backgroundColor: "transparent",
    textTransform: "none",
    "&:hover": {
      backgroundColor: UI_ACCENT_BLUE_HOVER,
    },
  },
  doneButtonWrapper: {
    position: "absolute",
    right: "0rem",
    bottom: "0rem",
  },
  listItemText: {
    margin: 0,
    textAlign: "center",
  },
  ampmSection: {
    display: "flex",
    flexDirection: "column",
    width: "3rem",
    paddingLeft: "0.5rem",
    position: "relative",
  },
  eddOutlinedFocus: {
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: `${UI_ACCENT_BLUE} !important`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: `${UI_ACCENT_BLUE} !important`,
    },
  },
  eddInlineInputFocus: {
    "&.Mui-focused": {
      borderColor: `${UI_ACCENT_BLUE} !important`,
    },
    "&:hover:not(.Mui-disabled)": {
      borderColor: `${UI_ACCENT_BLUE} !important`,
    },
  },
};

const styles = {
  input: css`
    cursor: pointer !important;
    border-radius: 4px;
    height: "2.5rem";
    width: 100%;
  `,
  inputProp: (isInline) => css`
    text-align: left;
    cursor: pointer;
    color: transparent !important;
    text-shadow: 0 0 0 ${Colors.grey_65};
    padding: ${isInline ? "0.61rem 0.875rem" : "0.67rem 0.875rem"} !important;
  `,
};

interface EDDSelectorProps {
  currentEDD: string;
  setCurrentEDD: Dispatch<SetStateAction<string>>;
  currentUserEDD?: EDD;
  patientID: number;
  isDischargeAM: boolean;
  setIsDischargeAM: Dispatch<SetStateAction<boolean>>;
  intelText?: IntelligenceText;
  enableDischargeTimeOfDay: boolean;
  isInline?: boolean;
  hasSuggestion?: boolean;
  analytics: AnalyticsBrowser;
  setShowErrorToast: Dispatch<SetStateAction<boolean>>;
  disabled?: boolean;
}

const EDDSelector: FC<EDDSelectorProps> = ({
  currentEDD,
  setCurrentEDD,
  currentUserEDD,
  patientID,
  isDischargeAM,
  setIsDischargeAM,
  intelText,
  enableDischargeTimeOfDay,
  isInline,
  hasSuggestion = false,
  analytics,
  setShowErrorToast,
  disabled,
}) => {
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);

  let currentSelectedEDD = null;
  if (currentUserEDD) {
    currentSelectedEDD = moment(currentUserEDD.edd_value);
  }
  useEffect(() => {
    if (currentUserEDD) setIsDischargeAM(currentUserEDD.is_am_discharge);
  }, [currentUserEDD, setIsDischargeAM]);

  // Also clear createdEddId when currentUserEDD updates (so we use the prop value)
  useEffect(() => {
    if (currentUserEDD) {
      setCreatedEddId(null); // Clear local state, use prop from now on
    }
  }, [currentUserEDD]);

  const [isOpen, setIsOpen] = useState(false);
  const [createdEddId, setCreatedEddId] = useState<number | null>(null);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  const patchMutation = useMutation(patchEDD, {
    onSuccess: (data) => {
      // Don't need to update optimistic data - server will provide real data
      queryClient.invalidateQueries({ queryKey: ["edd"] });
      setIsUpdating(false);

      // if we are in the patient list, we need to invalidate that as well
      if (isInline) {
        queryClient.invalidateQueries({
          queryKey: "eddlist",
        });
      }
    },
    onError: () => {
      // revert the change if the mutation fails
      setCurrentEDD(currentSelectedEDD);
      setIsUpdating(false);
      setShowErrorToast(true);
    },
  });

  const postMutation = useMutation(postEDD, {
    onSuccess: (data) => {
      setCreatedEddId(data.id); // Track the new EDD ID
      queryClient.invalidateQueries({ queryKey: ["edd"] });
      setIsUpdating(false);

      // if we are in the patient list, we need to invalidate that as well
      if (isInline) {
        queryClient.invalidateQueries({
          queryKey: "eddlist",
        });
      }
    },
    onError: () => {
      // revert the change if the mutation fails
      setCurrentEDD(currentSelectedEDD);
      setIsUpdating(false);
      setShowErrorToast(true);
    },
  });

  const hideIntelTextMutation = useMutation(hideIntelligenceText, {
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["intelligenceText"] });
    },
  });

  const handleSetEDD = (e, overrideIsAM?: boolean) => {
    if (disabled) return;
    if (isUpdating) return;
    if (!e) return;

    setIsUpdating(true);

    const edd = moment(e).format("YYYY-MM-DD");
    const isAM = overrideIsAM !== undefined ? overrideIsAM : isDischargeAM;

    // Check if we have an existing EDD (from props OR from a recent create)
    const existingEddId = currentUserEDD?.id || createdEddId;

    if (existingEddId) {
      patchMutation.mutate({
        id: existingEddId,
        edd_value: edd,
        status: currentUserEDD?.status || null,
        source: Sources.USER_CREATED,
        // clear intelligence fields
        suggested_patient_edd_id: null,
        decision_updated_ts: null,
        is_am_discharge: isAM,
      });
    } else {
      postMutation.mutate({
        edd_value: edd,
        source: Sources.USER_CREATED,
        edd_type: TypeChoices.USER_ENTERED,
        patient: patientID,
        suggested_patient_edd_id: null,
        status: null,
        is_am_discharge: isAM,
      });
    }

    // whenever we take an action, we also need to hide the intel text, if any
    if (intelText) {
      hideIntelTextMutation.mutate(intelText.id);
    }
    setCurrentEDD(e);

    analytics?.track(ActionTrack.EDD_INTERACTION, {
      edd_value: edd,
    });
  };

  const handleDischargeTimeChange = (isAM: boolean) => {
    if (disabled) return;
    setIsDischargeAM(isAM);
    // Only save if we have a date selected
    if (currentEDD || currentUserEDD) {
      handleSetEDD(currentEDD || currentSelectedEDD, isAM);
    }
  };

  const handleDoneClick = () => {
    setIsOpen(false);
  };

  const ActionList = () => {
    return (
      <Box sx={sxStyles.ampmSection}>
        <Box
          sx={{
            paddingLeft: "0.5rem",
          }}
        >
          <List sx={{ padding: 0 }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleDischargeTimeChange(true)}
                sx={{
                  padding: "0.5rem 0",
                  background: isDischargeAM ? UI_ACCENT_BLUE : Colors.white,
                  color: isDischargeAM ? Colors.white : Colors.grey_80,
                  "&:hover, &.Mui-focusVisible": {
                    backgroundColor: isDischargeAM
                      ? UI_ACCENT_BLUE
                      : UI_ACCENT_BLUE_HOVER,
                  },
                }}
              >
                <ListItemText primary={"AM"} sx={sxStyles.listItemText} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleDischargeTimeChange(false)}
                sx={{
                  padding: "0.5rem 0",
                  background: !isDischargeAM ? UI_ACCENT_BLUE : Colors.white,
                  color: !isDischargeAM ? Colors.white : Colors.grey_80,
                  "&:hover, &.Mui-focusVisible": {
                    backgroundColor: !isDischargeAM
                      ? UI_ACCENT_BLUE
                      : UI_ACCENT_BLUE_HOVER,
                  },
                }}
              >
                <ListItemText primary={"PM"} sx={sxStyles.listItemText} />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
        <Box sx={sxStyles.doneButtonWrapper}>
          <Button
            size="small"
            variant="text"
            sx={sxStyles.doneButton}
            onClick={handleDoneClick}
          >
            Done
          </Button>
        </Box>
      </Box>
    );
  };

  // shift the dates by 12 hours to move from AM -> PM for display
  const currentEDDWithMeridiem = !isDischargeAM
    ? moment(currentEDD).add(12, "hours")
    : moment(currentEDD);

  const currentSelectedEDDWithMeridiem = !isDischargeAM
    ? moment(currentSelectedEDD).add(12, "hours")
    : moment(currentSelectedEDD);

  const inputComponent = useCallback(
    (params) => {
      if (isInline) {
        return (
          <MuiBox
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Input
              style={{
                fontSize: "0.9rem",
                border: `1px solid ${Colors.grey_15}`,
                paddingLeft: "0.75rem",
                backgroundColor: Colors.white,
              }}
              sx={sxStyles.eddInlineInputFocus}
              ref={params.inputRef}
              {...(params.InputProps as MuiRootInputProps)}
              disabled={disabled}
              onClick={(e) => {
                if (!disabled) {
                  setIsOpen(!isOpen);
                }
              }}
              className={styles.input}
              disableUnderline
              inputProps={{
                ...params.inputProps,
                className: styles.inputProp(isInline),
                placeholder: "Select an EDD",
              }}
              endAdornment={
                <InputAdornment position="end" sx={sxStyles.inlineIcon}>
                  <EventIcon />
                </InputAdornment>
              }
            />
            {!disabled && params.InputProps?.endAdornment}
          </MuiBox>
        );
      }
      return (
        <Box sx={sxStyles.inputWrapper}>
          <FormControl variant="outlined" sx={{ width: "100%" }}>
            <InputLabel
              sx={{
                top: "9px",
                fontSize: "0.8rem",
                left: "12px",
              }}
              htmlFor="edd-input"
            >
              EDD
            </InputLabel>
            <OutlinedInput
              id="edd-input"
              ref={params.inputRef}
              {...(params.inputProps as QuilInputProps)}
              disabled={disabled}
              onClick={() => {
                if (!disabled) {
                  setIsOpen(!isOpen);
                }
              }}
              className={styles.input}
              style={{ fontSize: "0.9rem" }}
              sx={sxStyles.eddOutlinedFocus}
              label="EDD"
              notched
              inputProps={{
                className: styles.inputProp(isInline),
                placeholder: "Select an EDD",
              }}
              endAdornment={
                <QuilInputAdornment position="end" sx={sxStyles.icon}>
                  <EventIcon />
                </QuilInputAdornment>
              }
            />
            {!disabled && params.InputProps?.endAdornment}
          </FormControl>
        </Box>
      );
    },
    [isInline, isOpen, setIsOpen, disabled],
  );

  return (
    <DatePicker
      inputFormat={enableDischargeTimeOfDay ? "E MM/dd/yy, aa" : "E MM/dd/yy"}
      value={
        currentEDD ? currentEDDWithMeridiem : currentSelectedEDDWithMeridiem
      }
      onChange={(e) => handleSetEDD(e)}
      disableHighlightToday
      disableOpenPicker
      disableMaskedInput
      disablePast
      disabled={disabled}
      closeOnSelect={!enableDischargeTimeOfDay}
      views={["day"]}
      open={isOpen}
      onClose={() => setIsOpen(false)}
      components={{
        ActionBar: enableDischargeTimeOfDay ? ActionList : null,
      }}
      PaperProps={{
        sx: sxStyles.dayPicker,
      }}
      PopperProps={{
        placement: isInline ? "bottom" : "bottom-start",
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, hasSuggestion ? 42 : 0],
            },
          },
        ],
      }}
      renderInput={inputComponent}
    />
  );
};

export default EDDSelector;
